import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  RefreshCw,
  MessageCircle,
  Users,
  Sparkles,
  PenLine,
  ListChecks,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { TOPIC_LABELS } from "@/lib/mikrobiTopics";
import { MIKROBI_FAQ } from "@/data/mikrobiFaq";

interface QuestionRow {
  id: string;
  topic: string;
  lang: string;
  path: string | null;
  session_id: string | null;
  is_first: boolean;
  created_at: string;
  faq_category: string | null;
  faq_question: string | null;
  is_free_text: boolean | null;
}

const CATEGORY_LABEL = new Map<string, string>(MIKROBI_FAQ.map((c) => [c.id, c.de]));
const QUESTION_LABEL = new Map<string, string>(
  MIKROBI_FAQ.flatMap((c) => c.questions.map((q) => [`${c.id}/${q.id}`, q.de] as [string, string])),
);

const RANGES = [
  { label: "7 Tage", days: 7 },
  { label: "30 Tage", days: 30 },
  { label: "90 Tage", days: 90 },
  { label: "Alles", days: 3650 },
];

const fmt = (n: number) => new Intl.NumberFormat("de-AT").format(n);

const AdminMikrobi = () => {
  const [rows, setRows] = useState<QuestionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  const load = async (d: number) => {
    setLoading(true);
    setError(null);
    const since = new Date();
    since.setDate(since.getDate() - d);
    const { data, error } = await supabase
      .from("mikrobi_questions")
      .select(
        "id, topic, lang, path, session_id, is_first, created_at, faq_category, faq_question, is_free_text",
      )
      .gte("created_at", since.toISOString())
      .order("created_at", { ascending: false })
      .limit(5000)
      .returns<QuestionRow[]>();
    if (error) setError(error.message);
    setRows(data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const stats = useMemo(() => {
    const sessions = new Set(rows.map((r) => r.session_id ?? r.id));
    const first = rows.filter((r) => r.is_first).length;
    return {
      total: rows.length,
      sessions: sessions.size,
      first,
      perSession: sessions.size ? (rows.length / sessions.size).toFixed(1) : "0",
    };
  }, [rows]);

  const byDay = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r) => {
      const k = r.created_at.slice(0, 10);
      map.set(k, (map.get(k) ?? 0) + 1);
    });
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date: date.slice(5), count }));
  }, [rows]);

  const byTopic = useMemo(() => {
    const map = new Map<string, { count: number; de: number; en: number }>();
    rows.forEach((r) => {
      const entry = map.get(r.topic) ?? { count: 0, de: 0, en: 0 };
      entry.count += 1;
      if (r.lang === "en") entry.en += 1;
      else entry.de += 1;
      map.set(r.topic, entry);
    });
    return [...map.entries()]
      .map(([topic, v]) => ({ topic, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  const byPage = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r) => {
      const k = r.path || "(unbekannt)";
      map.set(k, (map.get(k) ?? 0) + 1);
    });
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
  }, [rows]);

  /** Angeklickte Startfragen aus dem geführten Katalog. */
  const byQuestion = useMemo(() => {
    const map = new Map<string, { count: number; de: number; en: number; category: string }>();
    rows
      .filter((r) => r.faq_question && r.faq_category)
      .forEach((r) => {
        const key = `${r.faq_category}/${r.faq_question}`;
        const entry = map.get(key) ?? { count: 0, de: 0, en: 0, category: r.faq_category! };
        entry.count += 1;
        if (r.lang === "en") entry.en += 1;
        else entry.de += 1;
        map.set(key, entry);
      });
    return [...map.entries()]
      .map(([key, v]) => ({
        key,
        label: QUESTION_LABEL.get(key) ?? key,
        categoryLabel: CATEGORY_LABEL.get(v.category) ?? v.category,
        ...v,
      }))
      .sort((a, b) => b.count - a.count);
  }, [rows]);

  /** Klicks je Themenbereich sowie Art der Frage (Startfrage / Folgefrage / Freitext). */
  const byCategory = useMemo(() => {
    const map = new Map<string, { start: number; follow: number; free: number }>();
    rows.forEach((r) => {
      const key = r.faq_category ?? "(ohne Bereich)";
      const entry = map.get(key) ?? { start: 0, follow: 0, free: 0 };
      if (r.is_free_text) entry.free += 1;
      else if (r.faq_question) entry.start += 1;
      else entry.follow += 1;
      map.set(key, entry);
    });
    return [...map.entries()]
      .map(([id, v]) => ({
        id,
        label: CATEGORY_LABEL.get(id) ?? id,
        ...v,
        total: v.start + v.follow + v.free,
      }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  const kinds = useMemo(() => {
    const start = rows.filter((r) => r.faq_question && !r.is_free_text).length;
    const free = rows.filter((r) => r.is_free_text).length;
    return { start, free, follow: rows.length - start - free };
  }, [rows]);



  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-10 max-w-6xl space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
              <Link to="/admin/blog">
                <ArrowLeft className="h-4 w-4 mr-2" /> Adminbereich
              </Link>
            </Button>
            <h1 className="text-3xl font-semibold">Mikrobi – Themen &amp; Nutzung</h1>
            <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
              Vollständig anonyme Auswertung: Es wird ausschließlich das Thema einer Frage gezählt –
              kein Fragetext, keine IP-Adresse, keine personenbezogenen Daten.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {RANGES.map((r) => (
              <Button
                key={r.days}
                size="sm"
                variant={days === r.days ? "default" : "outline"}
                onClick={() => setDays(r.days)}
              >
                {r.label}
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => load(days)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/admin/mikrobi/check">
                <ListChecks className="h-4 w-4 mr-2" /> Fragenkatalog &amp; Check
              </Link>
            </Button>
            <Button size="sm" asChild>
              <Link to="/admin/mikrobi/antworten">
                <PenLine className="h-4 w-4 mr-2" /> Antworten pflegen
              </Link>
            </Button>
          </div>
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                <MessageCircle className="h-4 w-4" /> Fragen gesamt
              </CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-semibold">{fmt(stats.total)}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" /> Gespräche
              </CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-semibold">{fmt(stats.sessions)}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                <Sparkles className="h-4 w-4" /> Einstiegsfragen
              </CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-semibold">{fmt(stats.first)}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Ø Fragen pro Gespräch
              </CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-semibold">{stats.perSession}</CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Nutzung im Zeitverlauf</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            {byDay.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Noch keine Daten im gewählten Zeitraum.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byDay}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Angeklickte Startfragen</CardTitle>
            <p className="text-sm text-muted-foreground">
              Zeigt, welche vorgefertigte Frage im gewählten Zeitraum wie oft angetippt wurde.
              Startfragen: {fmt(kinds.start)} · Folgefragen: {fmt(kinds.follow)} · frei getippt:{" "}
              {fmt(kinds.free)}
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Frage</TableHead>
                  <TableHead>Bereich</TableHead>
                  <TableHead className="text-right">Klicks</TableHead>
                  <TableHead className="text-right">DE</TableHead>
                  <TableHead className="text-right">EN</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byQuestion.map((q) => (
                  <TableRow key={q.key}>
                    <TableCell>{q.label}</TableCell>
                    <TableCell className="text-muted-foreground">{q.categoryLabel}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(q.count)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(q.de)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(q.en)}</TableCell>
                  </TableRow>
                ))}
                {byQuestion.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground text-sm">
                      Noch keine Klicks im gewählten Zeitraum.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Themenbereiche und Art der Frage</CardTitle>
            <p className="text-sm text-muted-foreground">
              Folgefragen sind die von Mikrobi vorgeschlagenen Anschluss-Chips, Freitext sind
              selbst getippte Fragen.
            </p>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bereich</TableHead>
                  <TableHead className="text-right">Startfragen</TableHead>
                  <TableHead className="text-right">Folgefragen</TableHead>
                  <TableHead className="text-right">Freitext</TableHead>
                  <TableHead className="text-right">Gesamt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byCategory.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.label}</TableCell>
                    <TableCell className="text-right">{fmt(c.start)}</TableCell>
                    <TableCell className="text-right">{fmt(c.follow)}</TableCell>
                    <TableCell className="text-right">{fmt(c.free)}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(c.total)}</TableCell>
                  </TableRow>
                ))}
                {byCategory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground text-sm">
                      Noch keine Daten im gewählten Zeitraum.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Häufigste Themen</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Thema</TableHead>
                  <TableHead className="text-right">Fragen</TableHead>
                  <TableHead className="text-right">DE</TableHead>
                  <TableHead className="text-right">EN</TableHead>
                  <TableHead className="w-40 text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byTopic.map((t) => (
                  <TableRow key={t.topic}>
                    <TableCell>{TOPIC_LABELS[t.topic] ?? t.topic}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(t.count)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(t.de)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmt(t.en)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" asChild>
                        <Link
                          to={`/admin/mikrobi/antworten?topic=${encodeURIComponent(
                            TOPIC_LABELS[t.topic] ?? t.topic,
                          )}`}
                        >
                          Antwort hinterlegen
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {byTopic.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground text-sm">
                      Noch keine Daten im gewählten Zeitraum.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Seiten, von denen aus gefragt wird</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Seite</TableHead>
                  <TableHead className="text-right">Fragen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byPage.map(([p, c]) => (
                  <TableRow key={p}>
                    <TableCell className="font-mono text-xs">{p}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(c)}</TableCell>
                  </TableRow>
                ))}
                {byPage.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-muted-foreground text-sm">
                      Noch keine Daten.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground">
          <Badge variant="outline" className="mr-2">
            Datenschutz
          </Badge>
          Der Wortlaut der Fragen wird nicht gespeichert. Frühere Volltext-Einträge wurden gelöscht.
        </p>
      </div>
    </div>
  );
};

export default AdminMikrobi;
